import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ListTree, FileText, Building2, Settings, LogOut, Menu as MenuIcon, X, Users, Sparkles, IndianRupee, Phone, Inbox, Image as ImageIcon, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { resolveAssetUrl } from "@/lib/api";
import { useSite } from "@/contexts/SiteContent";
import { SiteContentProvider } from "@/contexts/SiteContent";

const NAV = [
  { to: "/admin/leads", icon: LayoutDashboard, label: "Leads" },
  { to: "/admin/messages", icon: Inbox, label: "Messages" },
  { to: "/admin/agents", icon: Users, label: "Agents" },
  { to: "/admin/payouts", icon: IndianRupee, label: "Payouts" },
  { to: "/admin/content", icon: Sparkles, label: "Content" },
  { to: "/admin/menu", icon: ListTree, label: "Menu" },
  { to: "/admin/pages", icon: FileText, label: "Pages" },
  { to: "/admin/partners", icon: Building2, label: "Partners" },
  { to: "/admin/gallery", icon: ImageIcon, label: "Gallery" },
  { to: "/admin/twilio", icon: Phone, label: "Twilio / OTP" },
  { to: "/admin/deployment", icon: Download, label: "Deployment" },
];

function Sidebar({ onLink }) {
  const { content } = useSite();
  const brand = content?.branding || {};
  const logo = resolveAssetUrl(brand.logo_url || "");
  return (
    <>
      <div className="p-5 border-b border-slate-200 flex items-center gap-2.5">
        {logo && <img src={logo} alt="" className="h-9 w-auto"/>}
        <div>
          <div className="font-display font-extrabold text-sm">{brand.site_name || "TARA"} Admin</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted">Control room</div>
        </div>
      </div>
      <nav className="p-3 space-y-1 overflow-y-auto">
        {NAV.map(n => (
          <NavLink key={n.to} to={n.to} onClick={onLink}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s|\//g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-saffron/15 text-saffron" : "text-ink hover:bg-slate-100"}`
            }>
            <n.icon className="h-4 w-4" strokeWidth={1.8}/> {n.label}
          </NavLink>
        ))}
      </nav>
    </>
  );
}

function Inner() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col">
        <Sidebar/>
        <div className="mt-auto p-4 border-t border-slate-200">
          <div className="text-xs text-ink-muted truncate">{user?.email}</div>
          <Button data-testid="admin-logout" variant="ghost" onClick={async()=>{ await logout(); nav("/admin/login"); }} className="w-full justify-start mt-2 text-ink-muted hover:text-saffron">
            <LogOut className="h-4 w-4 mr-2"/> Logout
          </Button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={()=>setOpen(false)}/>
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white flex flex-col">
            <Sidebar onLink={() => setOpen(false)}/>
            <Button variant="ghost" onClick={async()=>{ await logout(); nav("/admin/login"); }} className="m-3 justify-start text-ink-muted">
              <LogOut className="h-4 w-4 mr-2"/> Logout
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
          <button data-testid="admin-mobile-menu" onClick={()=>setOpen(true)}>{open ? <X className="h-5 w-5"/> : <MenuIcon className="h-5 w-5"/>}</button>
          <div className="font-display font-bold">TARA Admin</div>
          <div className="w-5"/>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <Outlet/>
        </main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <SiteContentProvider>
      <Inner/>
    </SiteContentProvider>
  );
}
